import React, { useEffect, useState } from 'react';
import { X, Copy, Tag } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const COLOR_STYLES = {
  orange: { wrap: 'from-orange-600 via-amber-500 to-yellow-500', code: 'bg-white/95 text-orange-700' },
  rose: { wrap: 'from-rose-600 via-pink-500 to-fuchsia-500', code: 'bg-white/95 text-rose-700' },
  emerald: { wrap: 'from-emerald-600 via-teal-500 to-cyan-500', code: 'bg-white/95 text-emerald-700' },
  amber: { wrap: 'from-amber-600 via-orange-500 to-red-500', code: 'bg-white/95 text-amber-700' },
  violet: { wrap: 'from-violet-700 via-purple-600 to-fuchsia-500', code: 'bg-white/95 text-violet-700' },
};

function diff(now, target) {
  const ms = Math.max(0, target - now);
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return { d, h, m, s, total: ms };
}

const TimeBlock = ({ label, value }) => (
  <div className="flex flex-col items-center min-w-[44px]">
    <span data-testid={`festive-countdown-${label}`} className="text-xl md:text-2xl font-bold leading-none tabular-nums">
      {String(value).padStart(2, '0')}
    </span>
    <span className="text-[10px] uppercase tracking-widest opacity-80">{label}</span>
  </div>
);

export const FestiveBanner = () => {
  const [campaign, setCampaign] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    api.get('/campaigns/active').then((r) => setCampaign(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!campaign) return;
    // Hide if user already dismissed this specific campaign
    const dismissedId = localStorage.getItem('sb_festive_dismissed_id');
    if (dismissedId === campaign.id) setDismissed(true);
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [campaign]);

  if (!campaign || dismissed) return null;
  const target = new Date(campaign.end_date).getTime();
  const t = diff(now, target);
  if (t.total <= 0) return null;

  const palette = COLOR_STYLES[campaign.banner_color] || COLOR_STYLES.orange;

  const copyCode = async () => {
    if (!campaign.discount_code) return;
    try {
      await navigator.clipboard.writeText(campaign.discount_code);
      localStorage.setItem('sb_pending_coupon', campaign.discount_code);
      toast.success(`Code "${campaign.discount_code}" copied — auto-applies at checkout`);
    } catch {
      toast.error('Could not copy. Long-press to copy manually.');
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('sb_festive_dismissed_id', campaign.id);
    setDismissed(true);
  };

  return (
    <div
      data-testid="festive-banner"
      data-campaign-id={campaign.id}
      className={`relative bg-gradient-to-r ${palette.wrap} text-white`}
    >
      <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 md:gap-4 text-center md:text-left">
          <span className="text-3xl md:text-4xl" aria-hidden="true">{campaign.badge_emoji || '🎉'}</span>
          <div>
            <h3 data-testid="festive-title" className="font-bold text-base md:text-lg leading-tight">
              {campaign.title}
            </h3>
            {campaign.subtitle && (
              <p data-testid="festive-subtitle" className="text-sm opacity-95 leading-snug">
                {campaign.subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 md:gap-5">
          {campaign.discount_code && (
            <button
              data-testid="festive-code-btn"
              onClick={copyCode}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg font-mono font-bold text-sm shadow ${palette.code} hover:scale-105 transition`}
              aria-label={`Copy discount code ${campaign.discount_code}`}
            >
              <Tag size={14} />
              {campaign.discount_code}
              <Copy size={12} className="opacity-70" />
            </button>
          )}
          <div className="hidden sm:flex items-center gap-2 bg-black/15 rounded-lg px-3 py-2">
            <TimeBlock label="days" value={t.d} />
            <span className="opacity-50">:</span>
            <TimeBlock label="hrs" value={t.h} />
            <span className="opacity-50">:</span>
            <TimeBlock label="min" value={t.m} />
            <span className="opacity-50">:</span>
            <TimeBlock label="sec" value={t.s} />
          </div>
        </div>

        <button
          data-testid="festive-dismiss-btn"
          onClick={handleDismiss}
          className="absolute top-2 right-2 md:static text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10"
          aria-label="Dismiss banner"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};
